import { pageTitle } from '@palaccount/config';
import {
  MFG_MESSAGES,
  type WorkOrderResponse,
} from '@palaccount/contracts';
import { AlertTriangle, Check, Factory, Undo2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { Amount, Ltr, Pct, Qty } from '@/components/amount';
import { Gated, GatedGroup } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCan } from '@/lib/permissions';
import { localizeServerMessage, refusalMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { formatQty, formatQtyWithUnit } from '@/lib/format';

import { Modal } from '../inventory/modal';
import { useCompletionPlan, useWorkOrder, useWorkOrderMutations } from './api';
import { ManufacturingTabs } from './manufacturing-tabs';
import {
  completeLabel,
  signedAmount,
  stepperState,
  trimQty,
  varianceTone,
  visibleWorkstation,
  WORK_ORDER_STAGES,
} from './mfg-format';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MFG-02 — ONE production job, and ONE way to finish it.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * *Changed by owner ruling R1: the prototype drew a fourth region — a single
 * green one-click completion card that replaced the stepper and all three
 * tabs. It is gone. There is one completion flow (§9).*
 *
 * So this screen offers exactly four writes, in order — Reserve, Start, Log
 * output, Mark Complete — and one convenience that is a PRE-FILL: the
 * quick-complete panel asks the server what it would propose, shows the
 * figures, lets them be changed, and then runs the very same four. §9's own
 * sentence for the line between them is short enough to keep here: "pre-fill,
 * never skip. A value a user did not supply must at least be a value they saw
 * and could have changed."
 */
export function WorkOrderPage() {
  const { t } = useTranslation();
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const { data: job, isPending, isError } = useWorkOrder(workOrderId);
  const [tab, setTab] = useState<'materials' | 'routing' | 'completion'>('materials');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = pageTitle(job?.code ?? t('mfg.workOrders'));
  }, [t, job?.code]);

  if (isPending) {
    return (
      <div className="grid gap-4">
        <ManufacturingTabs />
        <div className="h-64 animate-pulse rounded-md bg-muted" aria-busy />
      </div>
    );
  }
  if (isError || job === undefined) {
    return (
      <div className="grid gap-4">
        <ManufacturingTabs />
        <p role="alert" className="py-8 text-center text-sm text-destructive">
          {t('common.loadFailed')}
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-4">
      <ManufacturingTabs />

      {error !== null ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      {/* ── Region 1: the status card and the five-stage stepper ─────────── */}
      <Card>
        <CardContent className="grid gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-lg font-bold">
              <Ltr value={job.code} className="font-mono" />
            </h2>
            <WorkOrderStatusPill status={job.status} />
            <span className="text-sm text-muted-foreground">
              {job.finishedItemName} ·{' '}
              {/* Field 4/5's meta line. §3's [decided at build time]: the BOM
                  reference is a REAL LINK, not display-only text. */}
              <Link to="/manufacturing" className="underline">
                {job.bomLabel}
              </Link>{' '}
              · {t('mfg.snapshotAtCreation')}
            </span>
          </div>

          {/* Field 6: the amber exception banner, carrying a REAL reason and a
              Resume action (§5.1's required addition). */}
          {job.exceptionReason !== null ? (
            <div className="flex items-center gap-3 rounded-md bg-amber-100 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <AlertTriangle className="size-4" aria-hidden />
              <span className="me-auto">{job.exceptionReason}</span>
              <ResumeButton job={job} onError={setError} />
            </div>
          ) : null}

          <ol className="flex flex-wrap items-center gap-2 overflow-x-auto">
            {WORK_ORDER_STAGES.map((stage, index) => {
              const state = stepperState(job.status, stage);
              return (
                <li key={stage} className="flex items-center gap-2">
                  <span
                    className={cn(
                      'flex size-7 items-center justify-center rounded-full text-xs font-semibold',
                      state === 'done' && 'bg-emerald-700 text-white',
                      state === 'current' && 'bg-primary text-primary-foreground',
                      // "A stage not yet reached shows an EMPTY grey circle
                      // with no number" (field 7).
                      state === 'pending' && 'border border-muted-foreground/30 text-transparent',
                    )}
                    aria-hidden={state === 'pending'}
                  >
                    {state === 'done' ? <Check className="size-4" /> : index + 1}
                  </span>
                  <span className="text-xs text-muted-foreground">{t(`mfg.stage.${stage}`)}</span>
                </li>
              );
            })}
          </ol>
        </CardContent>
      </Card>

      {/* ── Region 2: the header card ────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('mfg.workOrderDetails')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t('mfg.finishedItem')} value={job.finishedItemName} />
          {/* `N-4` — the job's target, in the finished item's stock unit (the
              shelf `BR-MFG-02-16` receives the good output onto). `Field`'s
              mono branch is an `<Ltr>`, so the composed pair travels as one
              island exactly as `<Qty>` would emit it. */}
          <Field
            label={t('mfg.qtyToProduce')}
            value={formatQtyWithUnit(job.qtyToProduce, job.outputUnitPrecision, job.outputUnitSymbol)}
            mono
          />
          <Field label={t('mfg.warehouse')} value={job.warehouseName} />
          <Field label={t('mfg.linkedSalesOrder')} value={job.linkedSalesOrder ?? '—'} mono />
          <Field label={t('mfg.plannedStart')} value={job.plannedStart} mono />
          <Field label={t('mfg.plannedEnd')} value={job.plannedEnd} mono />
          <Field label={t('mfg.priority')} value={t(`mfg.priority.${job.priority}`)} />
          <Field
            label={t('mfg.batchLot')}
            value={job.batchNo ?? '—'}
            mono
          />
        </CardContent>
      </Card>

      {/* ── Region 3: the three tabs. Materials opens first (§8.1). ──────── */}
      <nav className="flex flex-wrap gap-1 border-b pb-px" aria-label={job.code}>
        {(['materials', 'routing', 'completion'] as const).map((entry) => (
          <button
            key={entry}
            type="button"
            onClick={() => setTab(entry)}
            className={cn(
              'rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground',
              tab === entry && 'border-b-2 border-primary text-foreground',
            )}
          >
            {t(`mfg.woTab.${entry}`)}
          </button>
        ))}
      </nav>

      {tab === 'materials' ? <MaterialsTab job={job} onError={setError} /> : null}
      {tab === 'routing' ? <RoutingTab job={job} onError={setError} /> : null}
      {tab === 'completion' ? <CompletionTab job={job} onError={setError} /> : null}
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      {mono === true ? (
        <Ltr value={value} className="font-mono text-sm" />
      ) : (
        <p className="text-sm">{value}</p>
      )}
    </div>
  );
}

/** §7 — the pill. §5.1's [decided at build time]: it is RECOLOURED per status,
 *  because "a blue Cancelled pill will mislead people". */
export function WorkOrderStatusPill({ status }: { status: WorkOrderResponse['status'] }) {
  const { t } = useTranslation();
  const tone =
    status === 'completed' || status === 'closed'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
      : status === 'onhold'
        ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
        : status === 'cancelled'
          ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
          : status === 'draft'
            ? 'bg-muted text-muted-foreground'
            : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300';
  const label = t(`mfg.woStatus.${status}`);
  return (
    /**
     * The pill carries an explicit "Status: …" name, because the word alone is
     * ambiguous: the five-stage stepper right below prints the SAME words as
     * its stage labels, so a screen-reader user meets "Completed" twice with
     * nothing to say which one is the job's actual state and which is a
     * milestone it has passed. `role="status"` also means a change is
     * announced when the job moves, which is the one thing on this screen a
     * supervisor most wants to hear.
     */
    <span
      role="status"
      aria-label={t('mfg.statusIs', { status: label })}
      className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', tone)}
    >
      <Factory className="size-3" aria-hidden />
      {label}
    </span>
  );
}

function ResumeButton({
  job,
  onError,
}: {
  job: WorkOrderResponse;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const canEdit = useCan('manufacturing.edit');
  const { resume } = useWorkOrderMutations();
  if (job.status !== 'onhold' || !canEdit) return null;
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() =>
        void resume.mutateAsync(job.id).catch((caught: unknown) => onError(localizeServerMessage(t, refusalMessage(caught), caught)))
      }
    >
      {t('mfg.resume')}
    </Button>
  );
}

/** §8.1 — the action bar, the shortage banner and the six-row component table. */
function MaterialsTab({
  job,
  onError,
}: {
  job: WorkOrderResponse;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { reserve, unreserve, start } = useWorkOrderMutations();
  const [shortageOpen, setShortageOpen] = useState(false);

  // D46 (here and at every catch in this file): the cause rides along — a
  // structured refusal (wrongStage's tokens, belowProduced's figure)
  // localizes by key+params; anything else keeps the server's own sentence,
  // which is what PART 9 words.
  const run = (promise: Promise<unknown>) =>
    void promise.catch((caught: unknown) => onError(localizeServerMessage(t, refusalMessage(caught), caught)));

  return (
    <Card>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* §8.1: the shortage banner is PASSIVE — no button, no link, no
              fix. It never names the short component; the row chip does. */}
          {job.hasShortage ? (
            <span className="rounded-md bg-red-100 px-3 py-1.5 text-sm text-red-800 dark:bg-red-950 dark:text-red-300">
              {localizeServerMessage(t, MFG_MESSAGES.shortageBanner)}
            </span>
          ) : null}
          <span className="me-auto" />
          {job.materialsReserved ? (
            <>
              <span className="flex items-center gap-1 text-sm text-emerald-700 dark:text-emerald-400">
                <Check className="size-4" aria-hidden /> {t('mfg.materialsReserved')}
              </span>
              <Gated permission="manufacturing.edit" reasonKey="perm.cannot.reserveMaterials">
                <Button variant="outline" onClick={() => run(unreserve.mutateAsync(job.id))}>
                  {t('mfg.unreserve')}
                </Button>
              </Gated>
            </>
          ) : null}
          {/* "Reserve and Start never appear at the same time. They are two
              steps in a row: reserve, then start." */}
          {!job.materialsReserved && job.status === 'draft' ? (
            <Gated permission="manufacturing.edit" reasonKey="perm.cannot.reserveMaterials">
              <Button onClick={() => run(reserve.mutateAsync(job.id))}>
                {t('mfg.reserveMaterials')}
              </Button>
            </Gated>
          ) : null}
          {job.status === 'scheduled' ? (
            <Gated permission="manufacturing.edit" reasonKey="perm.cannot.runWorkOrders">
            <Button
              onClick={() => {
                // BR-MFG-02-07: a short job opens the modal FIRST. The server
                // refuses it either way, so this is where the answer is
                // collected — not where the rule lives.
                if (job.hasShortage) {
                  setShortageOpen(true);
                  return;
                }
                run(start.mutateAsync({ workOrderId: job.id }));
              }}
            >
              {t('mfg.startProduction')}
            </Button>
            </Gated>
          ) : null}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('mfg.colComponent')}</TableHead>
                <TableHead className="text-end">{t('mfg.colOnHand')}</TableHead>
                <TableHead className="text-end">{t('mfg.colRequired')}</TableHead>
                <TableHead className="text-end">{t('mfg.colReserved')}</TableHead>
                <TableHead className="text-end">{t('mfg.colConsumed')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {job.components.map((component) => (
                <TableRow
                  key={component.id}
                  className={cn(component.shortBy !== null && 'bg-red-50 dark:bg-red-950/30')}
                >
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {component.itemName}
                      {/* Field 20: the chip carries the SHORTFALL amount. */}
                      {component.shortBy !== null ? (
                        <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs text-white">
                          {t('mfg.shortBy', {
                            qty: formatQtyWithUnit(
                              component.shortBy,
                              component.unitPrecision,
                              component.unitSymbol,
                            ),
                          })}
                        </span>
                      ) : null}
                      {component.isOptional ? (
                        <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                          {t('mfg.colOptional')}
                        </span>
                      ) : null}
                    </div>
                    <Ltr
                      value={`${component.itemCode} · ${component.snapshotUnitCost}`}
                      className="font-mono text-xs text-muted-foreground"
                    />
                    {/* Field 21 / BR-MFG-02-10: permanent once applied. */}
                    {component.substitutedFromItemName !== null ? (
                      <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
                        {t('mfg.substitutedFrom', {
                          item: component.substitutedFromItemName,
                          reason: component.substitutionReason ?? '',
                        })}
                      </div>
                    ) : null}
                  </TableCell>
                  {/*
                    * `N-4` on all four, and the repetition is deliberate. The
                    * reverted "do not repeat what the cell beside it shows"
                    * case was ONE derived figure sitting next to its source
                    * (INV-06's running balance). These are four INDEPENDENT
                    * measurements — a shelf balance, a requirement, a
                    * reservation and a ledger net — each read on its own, on a
                    * table whose rows can be kilograms of housing and pieces of
                    * screw at once. `N-14`: "a quantity on a row must ALWAYS be
                    * shown with its unit."
                    *
                    * The unit is the COMPONENT LINE's, not the job's: a job
                    * making lamps in pieces may consume paint in litres.
                    */}
                  <TableCell className="text-end text-sm">
                    <Qty
                      value={component.onHand}
                      precision={component.unitPrecision}
                      unit={component.unitSymbol}
                    />
                  </TableCell>
                  <TableCell className="text-end text-sm">
                    <Qty
                      value={component.requiredQty}
                      precision={component.unitPrecision}
                      unit={component.unitSymbol}
                    />
                  </TableCell>
                  <TableCell className="text-end text-sm">
                    <Qty
                      value={component.reservedQty}
                      precision={component.unitPrecision}
                      unit={component.unitSymbol}
                    />
                  </TableCell>
                  <TableCell className="text-end text-sm">
                    <Qty
                      value={component.consumedQty}
                      precision={component.unitPrecision}
                      unit={component.unitSymbol}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* §5.3's permanent explanatory note, worded exactly. */}
        <p className="text-xs text-muted-foreground">{t('mfg.reservedVsConsumed')}</p>
      </CardContent>

      {shortageOpen ? (
        /**
         * §8.5. Exactly two buttons, and the second is AMBER rather than red:
         * "the design saying 'this is allowed but risky', not 'this is
         * forbidden'". The body never names the short component — "to find out
         * which part is short, the user must close the modal and read the red
         * chip on the row".
         */
        <Modal
          title={localizeServerMessage(t, MFG_MESSAGES.shortageTitle)}
          onClose={() => setShortageOpen(false)}
          className="max-w-md"
        >
          <p className="text-sm text-muted-foreground">
            {localizeServerMessage(t, MFG_MESSAGES.shortageBody)}
          </p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => setShortageOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              className="bg-amber-700 text-white hover:bg-amber-800"
              onClick={() => {
                setShortageOpen(false);
                run(start.mutateAsync({ workOrderId: job.id, acknowledgeShortage: true }));
              }}
            >
              {t('mfg.startAnyway')}
            </Button>
          </div>
        </Modal>
      ) : null}
    </Card>
  );
}

/** §5.4 — the operation table and its planned-against-actual roll-up. */
function RoutingTab({
  job,
  onError,
}: {
  job: WorkOrderResponse;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const canEdit = useCan('manufacturing.edit');
  const { updateOperation } = useWorkOrderMutations();
  const NEXT = { not_started: 'in_progress', in_progress: 'done', done: 'done' } as const;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className="me-auto">{t('mfg.routing')}</span>
          <span className="text-sm font-normal text-muted-foreground">
            {t('mfg.plannedActualMinutes', {
              planned: job.plannedMinutesTotal,
              actual: job.actualMinutesTotal,
            })}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {job.operations.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('mfg.noRoutingSteps')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('mfg.colOpStatus')}</TableHead>
                <TableHead>{t('mfg.colStep')}</TableHead>
                <TableHead className="text-end">{t('mfg.colPlannedMin')}</TableHead>
                <TableHead className="text-end">{t('mfg.colActualMin')}</TableHead>
                <TableHead>{t('mfg.colOperator')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {job.operations.map((operation) => (
                <TableRow key={operation.id} className="odd:bg-muted/30">
                  <TableCell>
                    {/* Field 22: ONE click-to-cycle button, and §5.4's decision
                        that it must NOT wrap — once Done it is disabled. */}
                    <Gated permission="manufacturing.edit" reasonKey="perm.cannot.runWorkOrders">
                    <Button
                      size="sm"
                      variant={operation.status === 'done' ? 'outline' : 'default'}
                      disabled={operation.status === 'done'}
                      onClick={() =>
                        void updateOperation
                          .mutateAsync({
                            workOrderId: job.id,
                            operationId: operation.id,
                            body: { status: NEXT[operation.status] },
                          })
                          .catch((caught: unknown) => onError(localizeServerMessage(t, refusalMessage(caught), caught)))
                      }
                    >
                      {t(`mfg.opStatus.${operation.status}`)}
                    </Button>
                    </Gated>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {operation.seq}. {operation.name}
                    </div>
                    <Ltr
                      value={visibleWorkstation(operation.workstation)}
                      className="font-mono text-xs text-muted-foreground"
                    />
                  </TableCell>
                  {/* Field 24, the CORRECTED way: recipe minutes × quantity. */}
                  <TableCell className="text-end font-mono text-sm">
                    {operation.plannedMinutes}
                  </TableCell>
                  <TableCell className="text-end">
                    <Input
                      aria-label={`${t('mfg.colActualMin')} ${operation.name}`}
                      type="number"
                      className="w-24 text-end font-mono"
                      defaultValue={operation.actualMinutes}
                      disabled={!canEdit}
                      onBlur={(event) =>
                        void updateOperation
                          .mutateAsync({
                            workOrderId: job.id,
                            operationId: operation.id,
                            body: { actualMinutes: Number(event.target.value) },
                          })
                          .catch((caught: unknown) => onError(localizeServerMessage(t, refusalMessage(caught), caught)))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`${t('mfg.colOperator')} ${operation.name}`}
                      className="w-40"
                      defaultValue={operation.operator ?? ''}
                      disabled={!canEdit}
                      onBlur={(event) =>
                        void updateOperation
                          .mutateAsync({
                            workOrderId: job.id,
                            operationId: operation.id,
                            body: { operator: event.target.value },
                          })
                          .catch((caught: unknown) => onError(localizeServerMessage(t, refusalMessage(caught), caught)))
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/** §8.3 — progress and logging on the left; cost and backflush on the right. */
function CompletionTab({
  job,
  onError,
}: {
  job: WorkOrderResponse;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const canEdit = useCan('manufacturing.edit');
  const { logOutput, reverseOutput, complete, close } = useWorkOrderMutations();

  const [good, setGood] = useState('');
  const [scrap, setScrap] = useState('');
  const [quickOpen, setQuickOpen] = useState(false);
  const { data: plan } = useCompletionPlan(job.id, quickOpen);

  const run = (promise: Promise<unknown>) =>
    void promise.catch((caught: unknown) => onError(localizeServerMessage(t, refusalMessage(caught), caught)));

  const tone = varianceTone(job.cost.variance);

  return (
    // NFR-17 — the completion tab was the OTHER layout the prototype fixed at
    // two equal halves with no narrow-window rule; below lg these stack.
    <div className="grid gap-4 lg:grid-cols-2">
      {/* ── Left: progress and logging ─────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('mfg.progress')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div>
            {/*
              * `N-4` on field 29's pair. The unit is written ONCE, after the
              * fraction, because "6 pcs / 8 pcs" is the repetition the rule
              * does not ask for: the two halves are the same measurement of
              * the same thing, and a fraction whose halves disagreed about
              * their unit would not be a fraction. One `<bdi dir="ltr">`
              * around the whole run, for the reason `<Qty>` is one island —
              * a number, a slash, a number and a symbol loose in an RTL
              * paragraph can be reordered past each other.
              */}
            <p className="text-sm">
              <Ltr
                value={`${formatQty(job.producedQty, job.outputUnitPrecision)} / ${formatQtyWithUnit(
                  job.qtyToProduce,
                  job.outputUnitPrecision,
                  job.outputUnitSymbol,
                )}`}
                className="font-mono tabular-nums"
              />{' '}
              · <Pct value={job.progressPct} />
            </p>
            {/* Field 30: "It stays green even when production goes over
                target. It does not recolour." */}
            <div className="mt-1 h-2.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-emerald-500"
                style={{ width: `${Math.min(100, job.progressPct)}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-muted-foreground">{t('mfg.goodQty')}</p>
              <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-400">
                <Qty
                  value={job.goodQty}
                  precision={job.outputUnitPrecision}
                  unit={job.outputUnitSymbol}
                />
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t('mfg.scrapQty')}</p>
              <p className="text-lg font-semibold text-red-600 dark:text-red-400">
                <Qty
                  value={job.scrapQty}
                  precision={job.outputUnitPrecision}
                  unit={job.outputUnitSymbol}
                />
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t('mfg.yield')}</p>
              <p className="font-mono text-lg font-semibold">
                {job.yieldPct === null ? '—' : <Pct value={trimQty(job.yieldPct)} />}
              </p>
            </div>
          </div>

          {job.status === 'inprogress' ? (
            <GatedGroup permission="manufacturing.edit" reasonKey="perm.cannot.runWorkOrders">
            <div className="grid gap-3 rounded-lg border p-3">
              <p className="text-sm font-medium">{t('mfg.logThisSession')}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="wo-good">{t('mfg.goodQty')}</Label>
                  <Input
                    id="wo-good"
                    className="font-mono"
                    placeholder="0"
                    value={good}
                    onChange={(event) => setGood(event.target.value)}
                  />
                </div>
                <div className="grid gap-1.5">
                  {/* BR-MFG-02-24: scrap is an INPUT, shown, never assumed. */}
                  <Label htmlFor="wo-scrap">{t('mfg.scrapQty')}</Label>
                  <Input
                    id="wo-scrap"
                    className="font-mono"
                    placeholder="0"
                    value={scrap}
                    onChange={(event) => setScrap(event.target.value)}
                  />
                </div>
              </div>
              <Button
                onClick={() => {
                  run(
                    logOutput
                      .mutateAsync({
                        workOrderId: job.id,
                        goodQty: good === '' ? '0' : good,
                        scrapQty: scrap === '' ? '0' : scrap,
                        entryMode: 'typed',
                      })
                      .then(() => {
                        setGood('');
                        setScrap('');
                      }),
                  );
                }}
              >
                {t('mfg.logOutput')}
              </Button>
              <Button variant="outline" onClick={() => setQuickOpen(true)}>
                {t('mfg.quickComplete')}
              </Button>
            </div>
            </GatedGroup>
          ) : null}

          {/* §5.5's REQUIRED ADDITION, built: one row per session, with a
              Reverse that posts an OPPOSITE session. "Without it, the good and
              scrap totals are unauditable." */}
          {job.outputLogs.length > 0 ? (
            <div>
              <p className="mb-2 text-sm font-medium">{t('mfg.outputLog')}</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.date')}</TableHead>
                    <TableHead className="text-end">{t('mfg.goodQty')}</TableHead>
                    <TableHead className="text-end">{t('mfg.scrapQty')}</TableHead>
                    <TableHead>{t('mfg.entryMode')}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {job.outputLogs.map((log) => (
                    <TableRow key={log.id} className={cn(log.isReversed && 'opacity-60')}>
                      <TableCell>
                        <Ltr value={log.loggedOn} className="font-mono text-xs" />
                      </TableCell>
                      {/* §5.5's audit rows. Every job in this table produces
                          the SAME finished item, so both columns are the job's
                          output unit — and a session log that could not be
                          read as a quantity is what "unauditable" means. */}
                      <TableCell className="text-end text-sm">
                        <Qty
                          value={log.goodQty}
                          precision={job.outputUnitPrecision}
                          unit={job.outputUnitSymbol}
                        />
                      </TableCell>
                      <TableCell className="text-end text-sm">
                        <Qty
                          value={log.scrapQty}
                          precision={job.outputUnitPrecision}
                          unit={job.outputUnitSymbol}
                        />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {/* BR-MFG-02-25: a quick completion stays
                            distinguishable from a counted one, for ever. */}
                        {t(`mfg.entryMode.${log.entryMode}`)}
                      </TableCell>
                      <TableCell>
                        {canEdit &&
                        job.status === 'inprogress' &&
                        !log.isReversed &&
                        log.entryMode !== 'reversal' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              run(reverseOutput.mutateAsync({ workOrderId: job.id, logId: log.id }))
                            }
                          >
                            <Undo2 aria-hidden /> {t('mfg.reverse')}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}

          {job.status === 'inprogress' ? (
            <Gated
              permission="manufacturing.edit"
              reasonKey="perm.cannot.runWorkOrders"
              className="w-full"
            >
              <Button
                className="w-full bg-emerald-700 text-white hover:bg-emerald-800"
                onClick={() => run(complete.mutateAsync(job.id))}
              >
                {/*
                  * §6: the label states the shortfall on its own face — and it
                  * is QUOTED, verbatim: "Its label shows the shortfall:
                  * `Mark Complete (Remaining 0)`". No unit goes inside it.
                  * Injecting one into a settled spec sentence is the mistake
                  * the display-rules wave already made once (`ERR-PUR-06-05`)
                  * and reverted: it stops being the sentence the spec wrote.
                  */}
                {completeLabel(t('mfg.markComplete'), job.remainingQty)}
              </Button>
            </Gated>
          ) : null}

          {/* §7 [built]: the prototype showed this stage and offered no
              control for it. Closing posts the manufacturing variance. */}
          {job.status === 'completed' ? (
            <Gated permission="manufacturing.approve" reasonKey="perm.cannot.closeWorkOrders">
              <Button variant="outline" onClick={() => run(close.mutateAsync(job.id))}>
                {t('mfg.closeJob')}
              </Button>
            </Gated>
          ) : null}
        </CardContent>
      </Card>

      {/* ── Right: cost variance, then the backflush switch ─────────────── */}
      <div className="grid content-start gap-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('mfg.actualVsPlanned')}</CardTitle>
          </CardHeader>
          <CardContent>
            {/* §8.3's fix: a job with no output shows a SENTENCE, never a
                card with a title and a blank body. */}
            {!job.cost.hasOutput ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t('mfg.nothingToCompare')}
              </p>
            ) : (
              <dl className="grid gap-2 text-sm">
                <CostRow label={t('mfg.plannedCost')} value={job.cost.plannedCost} />
                <CostRow label={t('mfg.actualMaterial')} value={job.cost.actualMaterial} muted />
                <CostRow label={t('mfg.actualLabor')} value={job.cost.actualLabor} muted />
                <CostRow label={t('mfg.actualCost')} value={job.cost.actualCost} />
                <div
                  className={cn(
                    'mt-2 flex items-center justify-between rounded-md p-3',
                    tone === 'over'
                      ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
                      : tone === 'under'
                        ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                        : 'bg-muted',
                  )}
                >
                  <dt className="font-medium">{t('mfg.variance')}</dt>
                  <dd className="font-mono text-lg font-bold">
                    {signedAmount(job.cost.variance)}
                  </dd>
                </div>
                {/* §12's split, which "the screen must let an accountant
                    name": the scrapped units, and the run over on parts and
                    time. They add to the leftover exactly. */}
                {job.status === 'completed' || job.status === 'closed' ? (
                  <div className="mt-2 grid gap-1 border-t pt-2 text-xs text-muted-foreground">
                    <CostRow label={t('mfg.overheadApplied')} value={job.cost.overheadApplied} muted />
                    <CostRow label={t('mfg.outputValue')} value={job.cost.outputValue} muted />
                    <CostRow label={t('mfg.closingVariance')} value={job.cost.closingVariance} muted />
                    <CostRow label={t('mfg.scrapValue')} value={job.cost.scrapValue} muted />
                    <CostRow label={t('mfg.overrunValue')} value={job.cost.overrunValue} muted />
                  </div>
                ) : null}
              </dl>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="grid gap-1 py-4">
            <p className="text-sm font-medium">
              {t('mfg.backflush')}: {job.backflush ? t('common.on') : t('common.off')}
            </p>
            {/* Field 45's hint, worded exactly. */}
            <p className="text-xs text-muted-foreground">{t('mfg.backflushHint')}</p>
          </CardContent>
        </Card>
      </div>

      {quickOpen ? (
        <QuickCompleteModal
          job={job}
          plan={plan}
          onClose={() => setQuickOpen(false)}
          onError={onError}
        />
      ) : null}
    </div>
  );
}

function CostRow({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className={cn('flex items-center justify-between', muted === true && 'text-muted-foreground')}>
      <dt>{label}</dt>
      <dd>
        <Amount value={value} />
      </dd>
    </div>
  );
}

/**
 * §9's quick-complete convenience — A PRE-FILL, AND ONLY A PRE-FILL.
 *
 * The figures come from the server's `completionPlan`, which writes nothing.
 * They are SHOWN and EDITABLE before anything happens, and pressing the button
 * runs `logOutput` — the same single path a step-by-step completion runs
 * (BR-MFG-02-23). The good quantity is the REMAINING quantity, never the
 * target (BR-MFG-02-14a), and scrap is proposed as zero rather than assumed to
 * be (BR-MFG-02-24).
 */
function QuickCompleteModal({
  job,
  plan,
  onClose,
  onError,
}: {
  job: WorkOrderResponse;
  plan: { proposedGoodQty: string; proposedScrapQty: string; hasShortage: boolean; shortItemNames: string[] } | undefined;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { logOutput } = useWorkOrderMutations();
  const [good, setGood] = useState<string | null>(null);
  const [scrap, setScrap] = useState<string | null>(null);

  const proposedGood = plan?.proposedGoodQty ?? '0';
  const proposedScrap = plan?.proposedScrapQty ?? '0';
  const goodValue = good ?? trimQty(proposedGood);
  const scrapValue = scrap ?? trimQty(proposedScrap);
  const touched = good !== null || scrap !== null;

  return (
    <Modal title={t('mfg.quickComplete')} onClose={onClose} className="max-w-md">
      <p className="text-sm text-muted-foreground">{t('mfg.quickCompleteHint')}</p>
      {plan?.hasShortage === true ? (
        <p className="mt-3 rounded-md bg-amber-100 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">
          {localizeServerMessage(t, MFG_MESSAGES.shortageBody)}
        </p>
      ) : null}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="quick-good">{t('mfg.goodQty')}</Label>
          <Input
            id="quick-good"
            className="font-mono"
            value={goodValue}
            onChange={(event) => setGood(event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="quick-scrap">{t('mfg.scrapQty')}</Label>
          <Input
            id="quick-scrap"
            className="font-mono"
            value={scrapValue}
            onChange={(event) => setScrap(event.target.value)}
          />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={() => {
            void logOutput
              .mutateAsync({
                workOrderId: job.id,
                goodQty: goodValue,
                scrapQty: scrapValue,
                // BR-MFG-02-25: the record keeps which figures a person typed
                // and which they merely confirmed.
                entryMode: touched ? 'typed' : 'prefilled',
              })
              .then(onClose)
              .catch((caught: unknown) => onError(localizeServerMessage(t, refusalMessage(caught), caught)));
          }}
        >
          {t('mfg.logOutput')}
        </Button>
      </div>
    </Modal>
  );
}
